import { Suspense, useCallback, useMemo, useState } from 'react'

import { SystemSpaceSlug } from '@bettermode/common/space'
import {
  Space,
  SpaceType,
  EntityListFilterByOperator,
} from '@tribeplatform/gql-client/types'
import { CollectionFormModal } from '@tribeplatform/react-components/Collection'
import { T, useI18n } from '@tribeplatform/react-components/i18n'
import { useNavigate } from '@tribeplatform/react-components/Link'
import {
  useSpaces,
  useNetwork,
  useCollections,
} from '@tribeplatform/react-sdk/hooks'
import { simplifyPaginatedResult } from '@tribeplatform/react-sdk/utils'
import { SimpleDivider } from '@tribeplatform/react-ui-kit/Divider'
import { SvgIcon } from '@tribeplatform/react-ui-kit/Icon'
import { SearchInput } from '@tribeplatform/react-ui-kit/Input'
import { Link as UILink } from '@tribeplatform/react-ui-kit/Link'
import { LoadingOverlay } from '@tribeplatform/react-ui-kit/LoadingOverlay'
import { NavVertical } from '@tribeplatform/react-ui-kit/Sidebar'
import { Tooltip } from '@tribeplatform/react-ui-kit/Tooltip'

import { CollectionItem } from './components/CollectionItem.js'
import { SpaceItem } from './components/SpaceItem.js'

const SidebarMenuLoading = () => {
  return (
    <div className="animate-pulse flex space-s-4 mt-3 mx-1 px-2">
      <div className="space-y-3 flex-1">
        <div className="h-4 bg-skeleton rounded-full w-3/4" />
        <div className="h-4 bg-skeleton rounded-full w-11/12" />
        <div className="h-4 bg-skeleton rounded-full w-5/6" />
        <div className="h-4 bg-skeleton rounded-full w-4/6" />
        <div className="h-4 bg-skeleton rounded-full w-11/12" />
        <div className="h-4 bg-skeleton rounded-full w-5/6" />
      </div>
    </div>
  )
}

const WHITELIST_SPACE_SLUGS: string[] = [
  SystemSpaceSlug.HOME,
  SystemSpaceSlug.MEMBER,
]

export const ManageSpacesListSidebar = () => {
  const { $t } = useI18n()
  const navigate = useNavigate()
  const [openCollectionModal, setOpenCollectionModal] = useState(false)
  const [query, setQuery] = useState('')
  const { data: network } = useNetwork()
  const {
    data: paginatedNoCollectionSpaces,
    isInitialLoading: isNoCollectionSpacesLoading,
  } = useSpaces({
    fields: {
      image: 'basic',
      collection: 'basic',
      slate: 'all',
    },
    variables: {
      limit: 500,
      type: [SpaceType.Group, SpaceType.Broadcast],
      filterBy: [
        {
          key: 'groupId',
          operator: EntityListFilterByOperator.hasValue,
          value: 'false',
        },
      ],
    },
  })

  const { nodes: noCollectionSpaces } = simplifyPaginatedResult<Space>(
    paginatedNoCollectionSpaces,
  )

  const utilitySpaces = network.systemSpaces.filter(space =>
    WHITELIST_SPACE_SLUGS.includes(space.slug),
  )

  const { data: collections, isInitialLoading: isCollectionsLoading } =
    useCollections({
      fields: {
        spaces: {
          fields: {
            image: 'basic',
            banner: 'basic',
            slate: 'all',
            authMemberProps: {},
          },
          variables: {
            limit: 100,
          },
        },
      },
    })

  const filteredSpaces = useMemo(() => {
    if (!collections || !paginatedNoCollectionSpaces || !utilitySpaces) {
      return []
    }

    if (!query) {
      return []
    }

    const allCollectionSpaces = collections.flatMap(
      collection => collection.spaces.nodes,
    )
    const allSpaces = [
      ...allCollectionSpaces,
      ...noCollectionSpaces,
      ...utilitySpaces,
    ]
    const uniqueSpaces = allSpaces.filter(space => {
      const index = allSpaces.findIndex(({ id }) => id === space.id)
      return index === allSpaces.indexOf(space)
    })

    return uniqueSpaces.filter(space =>
      space.name?.toLowerCase().includes(query.toLowerCase()),
    )
  }, [
    collections,
    query,
    noCollectionSpaces,
    paginatedNoCollectionSpaces,
    utilitySpaces,
  ])

  const isLoading =
    isCollectionsLoading ||
    isNoCollectionSpacesLoading ||
    !paginatedNoCollectionSpaces ||
    !collections

  const openCreateSpaceModal = useCallback(() => {
    navigate(`/manage/customizer/create-space`)
  }, [navigate])

  return (
    <>
      <div className="space-y-5">
        <SearchInput
          value={query}
          onChange={e => setQuery(e.target.value)}
          size="compact"
        />
        {isLoading && <SidebarMenuLoading />}
        {!!collections && !!noCollectionSpaces && (
          <LoadingOverlay
            isLoading={isCollectionsLoading || isNoCollectionSpacesLoading}
            pulse
          >
            <NavVertical>
              {query ? (
                <NavVertical.Group>
                  {filteredSpaces.map(space => (
                    <SpaceItem key={space.id} space={space} />
                  ))}
                  {filteredSpaces.length === 0 && (
                    <div className="text-md ps-1">
                      <T
                        id="Generics.SpaceNotFound"
                        defaultMessage="{SPACE_CC} not found!"
                      />
                    </div>
                  )}
                </NavVertical.Group>
              ) : (
                <NavVertical.Group>
                  {collections.map(({ id, name, description, spaces }) => (
                    <CollectionItem
                      key={id}
                      id={id}
                      name={name}
                      description={description}
                      spaces={spaces.nodes}
                    />
                  ))}
                  {noCollectionSpaces.length > 0 && (
                    <CollectionItem
                      id={null}
                      name={$t({
                        id: 'Generics.NoCollection',
                        defaultMessage: 'No {COLLECTION}',
                      })}
                      spaces={noCollectionSpaces}
                    />
                  )}
                  {utilitySpaces.length > 0 && (
                    <CollectionItem
                      id={null}
                      name={$t({
                        id: 'Generics.UtilitySpaces',
                        defaultMessage: 'Utility {SPACES}',
                      })}
                      spaces={utilitySpaces}
                      noAddSpace
                      showDivider={false}
                    />
                  )}
                </NavVertical.Group>
              )}
            </NavVertical>

            <div className="sticky bg-surface -bottom-5 -mb-10 pb-1">
              {!query && <SimpleDivider className="my-1" variant="subdued" />}
              {!query && (
                <NavVertical.Item
                  as={UILink}
                  leadingIcon={<SvgIcon name="package-plus" size="lg" />}
                  onClick={openCreateSpaceModal}
                  className="mb-1"
                >
                  <div className="flex-1 truncate">
                    <T defaultMessage="Add {SPACE}" id="Generics.AddSpace" />

                    <Tooltip>
                      <Tooltip.Trigger className="inline-block">
                        <SvgIcon
                          inline
                          className="ms-1 shrink-0"
                          name="alert-circle"
                          size="sm"
                        />
                      </Tooltip.Trigger>
                      <Tooltip.Panel>
                        <T
                          defaultMessage="Create a versatile area for discussions, content, pages, or groups."
                          id="Admin.CollectionsAndSpaces.CreateSpaceTooltip"
                        />
                      </Tooltip.Panel>
                    </Tooltip>
                  </div>
                </NavVertical.Item>
              )}
              {!query && (
                <NavVertical.Item
                  as={UILink}
                  leadingIcon={<SvgIcon name="file-plus-01" size="lg" />}
                  onClick={() => setOpenCollectionModal(true)}
                  className="mt-1"
                >
                  <div className="flex-1 truncate">
                    <T
                      defaultMessage="Add {COLLECTION}"
                      id="Generics.AddCollection"
                    />
                    <Tooltip>
                      <Tooltip.Trigger className="inline-block">
                        <SvgIcon
                          inline
                          className="ms-1 shrink-0"
                          name="alert-circle"
                          size="sm"
                        />
                      </Tooltip.Trigger>
                      <Tooltip.Panel>
                        <T
                          defaultMessage="Group multiple spaces together into a collection for better organization."
                          id="Admin.CollectionsAndSpaces.CreateCollectionTooltip"
                        />
                      </Tooltip.Panel>
                    </Tooltip>
                  </div>
                </NavVertical.Item>
              )}
            </div>
          </LoadingOverlay>
        )}
      </div>
      {openCollectionModal && (
        <Suspense fallback={<></>}>
          <CollectionFormModal
            open={openCollectionModal}
            onClose={() => {
              setOpenCollectionModal(false)
            }}
          />
        </Suspense>
      )}
    </>
  )
}
